import { formatPostalCode } from "@/Helpers/formatPostalCode";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import type {
  BookShipmentSlice2Type,
  ShipmentDraftState,
} from "@/Shared/Types/bookShipmentSliceTypes";
import { bookShipmentFindSavedLocationIndex } from "../Helpers/bookShipmentFindSavedLocationIndex";
import { initialBookShipmentSliceState } from "../Helpers/bookShipmentEmptyState";
import { isBlank } from "@freightsimple/shared/Helpers/isNotBlank";
import { isCrossBorder } from "@/Helpers/isCrossBorder";
import { bookShipmentDropHiddenDeadline } from "./bookShipmentDropHiddenDeadline";
import { bookShipmentShouldShowDeadlineSection } from "./bookShipmentShouldShowDeadlineSection";

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function bookShipmentLoadPreBookingState({
  state,
  shipment,
  savedLocations,
}: {
  state: BookShipmentSlice2Type;
  shipment: PreBookingShipment;
  savedLocations: Array<SavedLocation>;
}): BookShipmentSlice2Type {
  const previouslyCompletedFlowItems = [...state.completedFlowItems];
  const preserveCompletedFlowItems =
    state.shipment.shipmentId === shipment.shipmentId;

  const newState = initialBookShipmentSliceState();

  newState.shipment = shipment;

  // Preserve draft if shipment was created with current draft value
  if (state.draft && shipment.shipmentId === state.draft.shipmentId) {
    const crossBorder = isCrossBorder(
      shipment.pickupLocation.address.countryCode,
      shipment.deliveryLocation.address.countryCode,
    );

    newState.draft = {
      ...state.draft,
      broker: crossBorder ? { ...state.draft.broker } : undefined,
    } as ShipmentDraftState;
  }

  const draft = newState.draft;

  // The postal code which comes from the shipment isn't formatted
  if (!isBlank(shipment.pickupLocation.address.postalCode)) {
    newState.shipment.pickupLocation.address.postalCode = formatPostalCode(
      shipment.pickupLocation.address.postalCode,
    );
  }

  if (!isBlank(shipment.deliveryLocation.address.postalCode)) {
    newState.shipment.deliveryLocation.address.postalCode = formatPostalCode(
      shipment.deliveryLocation.address.postalCode,
    );
  }

  const pickupIndex = bookShipmentFindSavedLocationIndex(
    shipment.pickupLocation,
    savedLocations,
  );

  const deliveryIndex = bookShipmentFindSavedLocationIndex(
    shipment.deliveryLocation,
    savedLocations,
  );

  if (pickupIndex !== -1) {
    newState.enteringNewPickup = false;
    newState.selectedPickupSavedLocationId =
      savedLocations[pickupIndex]?.savedLocationId;
  } else {
    newState.enteringNewPickup = true;
    newState.selectedPickupSavedLocationId = undefined;
  }

  if (deliveryIndex !== -1) {
    newState.enteringNewDelivery = false;
    newState.selectedDeliverySavedLocationId =
      savedLocations[deliveryIndex]?.savedLocationId;
  } else {
    newState.enteringNewDelivery = true;
    newState.selectedDeliverySavedLocationId = undefined;
  }

  newState.isModifying = false;

  if (preserveCompletedFlowItems) {
    newState.completedFlowItems = previouslyCompletedFlowItems;
  }

  function ensureCompletedItem(item: string) {
    if (!newState.completedFlowItems.includes(item)) {
      newState.completedFlowItems.push(item);
    }
  }

  ensureCompletedItem("details");

  if (newState.shipment.selectedQuote) {
    ensureCompletedItem("quotes");
  }

  if (newState.shipment.isReviewingPriorToBookingShipment) {
    ensureCompletedItem("pickup-address");
    ensureCompletedItem("delivery-address");
    ensureCompletedItem("broker");
    ensureCompletedItem("payment");
  }

  // A shipment quoted earlier can still carry a deadline its location no longer
  // accepts, so drop it rather than let it ride along to booking unseen
  bookShipmentDropHiddenDeadline(newState.shipment, LocationContext.Pickup);
  bookShipmentDropHiddenDeadline(newState.shipment, LocationContext.Delivery);

  // Apply draft values if necessary
  if (!draft) return newState;

  // Only add broker draft info if shipment is crossborder
  //  DO NOT APPLY BROKER DRAFT VALUES HERE
  // const isCrossBorder =
  //   shipment.pickupLocation.address.countryCode !=
  //   shipment.deliveryLocation.address.countryCode;
  // if (isCrossBorder && !shipment.broker && draft.broker) {
  //   newState.shipment.broker = {
  //     ...draft.broker,
  //     address: { ...draft.broker?.address },
  //     contact: { ...draft.broker?.contact },
  //   };
  // }

  // Only add emergency contact draft info if has dangerous goods
  if (
    shipment.lineItems.some((l) => l.isDangerous) &&
    !shipment.emergencyContactDetails &&
    draft.emergencyContact
  ) {
    newState.shipment.emergencyContactDetails = {
      contactName: draft.emergencyContact.contactName ?? "",
      contactPhoneNumber: draft.emergencyContact.contactPhoneNumber ?? "",
      contactPhoneNumberExtension:
        draft.emergencyContact.contactPhoneNumberExtension ?? "",
      ...(draft.emergencyContact.erapPlanNumber
        ? { erapPlanNumber: draft.emergencyContact.erapPlanNumber }
        : {}),
      ...(draft.emergencyContact.erapPhoneNumber
        ? { erapPhoneNumber: draft.emergencyContact.erapPhoneNumber }
        : {}),
      ...(draft.emergencyContact.tdgResponsibleName
        ? { tdgResponsibleName: draft.emergencyContact.tdgResponsibleName }
        : {}),
    };
  }

  if (draft.pickup) {
    const { deadline, boothNumber } = draft.pickup;

    // Restoring a deadline the address screen won't show would leave it hidden
    // on the shipment, so this asks the same question the screen does
    const shouldUseDeadline = bookShipmentShouldShowDeadlineSection(
      newState.shipment.pickupLocation,
      LocationContext.Pickup,
    );

    const shouldUseBoothNumber =
      shipment.pickupLocation.locationType === LocationType.TradeShow;

    if (shouldUseDeadline && !shipment.pickupDeadline && deadline) {
      newState.shipment.pickupDeadline = draft.pickup.deadline
        ?.toISOString()
        .split("T")[0];
    }

    if (shouldUseBoothNumber && !shipment.pickupBoothNumber && boothNumber) {
      newState.shipment.pickupBoothNumber = draft.pickup.boothNumber;
    }

    if (!shipment.pickupContact) {
      newState.shipment.pickupContact = {
        contactName: draft.pickup.contactName || "",
        phoneNumber: draft.pickup.contactPhoneNumber || "",
        phoneNumberExtension: draft.pickup.contactPhoneNumberExtension || "",
        emailAddress: draft.pickup.contactEmail || "",
      };
    }
  }

  if (draft.delivery) {
    const { deadline, boothNumber } = draft.delivery;

    const shouldUseDeadline = bookShipmentShouldShowDeadlineSection(
      newState.shipment.deliveryLocation,
      LocationContext.Delivery,
    );

    const shouldUseBoothNumber =
      shipment.deliveryLocation.locationType === LocationType.TradeShow;

    if (shouldUseDeadline && !shipment.deliveryDeadline && deadline) {
      newState.shipment.deliveryDeadline = draft.delivery.deadline
        ?.toISOString()
        .split("T")[0];
    }

    if (shouldUseBoothNumber && !shipment.deliveryBoothNumber && boothNumber) {
      newState.shipment.deliveryBoothNumber = draft.delivery.boothNumber;
    }

    if (!shipment.deliveryContact) {
      newState.shipment.deliveryContact = {
        contactName: draft.delivery.contactName || "",
        phoneNumber: draft.delivery.contactPhoneNumber || "",
        phoneNumberExtension: draft.delivery.contactPhoneNumberExtension || "",
        emailAddress: draft.delivery.contactEmail || "",
      };
    }
  }

  return newState;
}
